<template>
  <Modal 
    :visible="visible" 
    title="解决异常注单"
    maxWidth="lg"
    :close-on-backdrop="!submitting"
    @close="handleClose"
  >
    <div v-if="abnormalId" class="mb-4 p-2">
      <div class="flex items-center mb-4">
        <div class="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-green-100 mr-4">
          <svg class="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </div>
        <p class="text-sm text-gray-500">
          您正在将注单 <span class="font-medium">{{ fundsn }}</span> 的异常标记为已解决。
        </p>
      </div>

      <div class="bg-gray-50 p-4 rounded-md mb-4">
        <h4 class="text-sm font-medium text-gray-700 mb-3">异常记录</h4>
        <div class="space-y-4">
          <div class="flex items-start border-l-2 border-blue-500 pl-3 py-1 relative">
            <span class="absolute left-[-5px] top-0 w-2 h-2 rounded-full bg-blue-500"></span>
            <div class="flex-1">
              <p class="text-sm text-gray-700">
                <span class="font-semibold">创建记录</span>
                <span class="text-xs text-gray-500 ml-2">{{ abnormalInfo.created_at || '加载中...' }}</span>
              </p>
              <p class="text-sm text-gray-800 ml-4">{{ abnormalInfo.reason_text || '加载中...' }}</p>
              <p class="text-xs text-gray-500 ml-4">{{ abnormalInfo.description || '' }}</p>
              <p class="text-xs text-gray-500 ml-4">由 {{ abnormalInfo.admin_name || '系统' }} 标记</p>
            </div>
          </div>
        </div>
      </div>

      <div class="mb-4">
        <label for="resolution_notes" class="block text-sm font-medium text-gray-700 mb-1">解决说明</label>
        <textarea 
          v-model="resolutionNotes" 
          id="resolution_notes" 
          rows="3" 
          class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm" 
          placeholder="请提供问题解决方案和处理过程的详细描述..."
          :disabled="submitting"
        ></textarea>
        <p class="mt-1 text-xs text-gray-500">请详细描述如何解决了这个问题，以便于后续跟踪。</p>
      </div>
      
      <div v-if="error" class="mt-2 text-sm text-red-600">{{ error }}</div>
    </div>

    <template #footer>
      <button 
        @click="handleClose" 
        :disabled="submitting" 
        type="button" 
        class="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:w-auto sm:text-sm mr-3"
        :class="{ 'opacity-75 cursor-not-allowed': submitting }"
      >
        取消
      </button>
      <button 
        @click="submit" 
        :disabled="submitting || !resolutionNotes" 
        type="button" 
        class="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:w-auto sm:text-sm"
        :class="{ 'opacity-75 cursor-not-allowed': submitting || !resolutionNotes }"
      >
        <template v-if="submitting">
          <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          提交中...
        </template>
        <template v-else>标记为已解决</template>
      </button>
    </template>
  </Modal>
</template>

<script setup>
import { ref, reactive, watch } from 'vue';
import Modal from '@/components/common/Modal.vue';
import AdminApiClient from '@/utils/admin-api-client';

const apiClient = new AdminApiClient();

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  abnormalId: {
    type: [String, Number],
    default: ''
  },
  fundsn: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['close', 'submit-success']);

// 状态变量
const resolutionNotes = ref('');
const error = ref('');
const submitting = ref(false);
const abnormalInfo = reactive({
  created_at: '',
  reason: '',
  reason_text: '',
  description: '',
  admin_name: ''
});

// 监听可见性变化
watch(() => props.visible, async (newVal) => {
  if (newVal && props.abnormalId) {
    resetForm();
    await loadAbnormalInfo();
  }
});

// 重置表单
function resetForm() {
  resolutionNotes.value = '';
  error.value = '';
  submitting.value = false;
  Object.keys(abnormalInfo).forEach(key => {
    abnormalInfo[key] = '';
  });
}

// 加载异常信息
async function loadAbnormalInfo() {
  try {
    // 使用API客户端获取异常详情
    const response = await apiClient.getAbnormalBets({ id: props.abnormalId });
    
    if (response && response.success) {
      const abnormalItems = response.data.items || [];
      
      if (abnormalItems.length > 0) {
        const data = abnormalItems[0];
        abnormalInfo.created_at = data.created_at || '';
        abnormalInfo.reason = data.reason || '';
        abnormalInfo.reason_text = getAbnormalReasonText(data.reason);
        abnormalInfo.description = data.description || '';
        abnormalInfo.admin_name = data.admin_name || '系统';
      } else {
        error.value = '未找到异常记录';
      }
    } else {
      error.value = response?.message || '加载异常记录失败';
    }
  } catch (e) {
    console.error('加载异常记录出错:', e);
    error.value = e.message || '加载异常记录失败，请稍后重试';
  }
}

// 获取异常原因文本
function getAbnormalReasonText(reason) {
  const reasonMap = {
    'system_error': '系统错误',
    'amount_error': '金额错误',
    'betting_anomaly': '投注异常',
    'other_issue': '其他问题'
  };
  
  return reasonMap[reason] || reason || '未知原因';
}

// 关闭模态框
function handleClose() {
  if (submitting.value) return;
  emit('close');
}

// 提交解决异常请求
async function submit() {
  if (!resolutionNotes.value) {
    error.value = '请填写解决说明';
    return;
  }
  
  if (resolutionNotes.value.length < 5) {
    error.value = '解决说明不能少于5个字符';
    return;
  }
  
  if (!props.abnormalId) {
    error.value = '缺少异常ID';
    return;
  }
  
  submitting.value = true;
  error.value = '';
  
  try {
    // 构建请求数据
    const formData = {
      abnormal_id: props.abnormalId,
      resolution_notes: resolutionNotes.value
    };
    
    // 使用API客户端解决异常
    const response = await apiClient.resolveAbnormalBet(formData);
    
    // 处理响应
    if (response && response.success) {
      // 通知父组件提交成功
      emit('submit-success', {
        id: props.abnormalId,
        fundsn: props.fundsn,
        resolution_notes: resolutionNotes.value
      });
      
      // 关闭模态框
      emit('close');
      
      // 显示成功提示
      alert('异常解决成功');
    } else {
      error.value = response?.message || '解决失败';
    }
  } catch (e) {
    console.error('解决异常注单出错:', e);
    error.value = e.message || '解决失败，请稍后重试';
  } finally {
    submitting.value = false;
  }
}
</script> 